import { create } from 'zustand'
import type { AssistantPlan } from './types'
import type { AssistantJob } from './types'
import type { VoiceoverVideoAssistantContext } from './planner/voiceover-video-planner'

type AssistantContext = VoiceoverVideoAssistantContext & Record<string, unknown>

interface AssistantJobStoreState {
  draftPlan: AssistantPlan<AssistantContext> | null
  currentJob: AssistantJob<AssistantContext> | null
  lastCompletedJob: AssistantJob<AssistantContext> | null
  isRunning: boolean
  planningPrompt: string | null
  error: string | null
}

interface AssistantJobStoreActions {
  startPlanning: (prompt: string) => void
  setDraftPlan: (plan: AssistantPlan<AssistantContext>) => void
  startRun: () => void
  updateJob: (job: AssistantJob<AssistantContext>) => void
  finishRun: (job: AssistantJob<AssistantContext>) => void
  failRun: (message: string) => void
  clear: () => void
}

export const useAssistantJobStore = create<AssistantJobStoreState & AssistantJobStoreActions>(
  (set) => ({
    draftPlan: null,
    currentJob: null,
    lastCompletedJob: null,
    isRunning: false,
    planningPrompt: null,
    error: null,

    startPlanning: (prompt) =>
      set({
        draftPlan: null,
        currentJob: null,
        planningPrompt: prompt,
        isRunning: true,
        error: null,
      }),

    setDraftPlan: (plan) =>
      set({
        draftPlan: plan,
        currentJob: null,
        isRunning: false,
        error: null,
      }),

    startRun: () =>
      set((state) => ({
        draftPlan: state.draftPlan,
        currentJob: null,
        isRunning: true,
        error: null,
      })),

    updateJob: (job) =>
      set({
        currentJob: job,
        isRunning: job.status === 'running' || job.status === 'queued',
        error: job.status === 'failed' ? job.steps.find((step) => step.status === 'failed')?.errorMessage ?? null : null,
      }),

    finishRun: (job) =>
      set({
        draftPlan: null,
        currentJob: job,
        lastCompletedJob: job,
        isRunning: false,
        planningPrompt: null,
        error: job.status === 'failed' ? job.steps.find((step) => step.status === 'failed')?.errorMessage ?? null : null,
      }),

    failRun: (message) =>
      set({
        isRunning: false,
        error: message,
      }),

    clear: () =>
      set({
        draftPlan: null,
        currentJob: null,
        lastCompletedJob: null,
        isRunning: false,
        planningPrompt: null,
        error: null,
      }),
  }),
)
